/*
  Variant content refresh (Base Product).

  The Base Product sections read their metaobject from the selected variant
  first, then the product. When the shopper switches variant, product-info.js
  only updates price, SKU and the like - so this file refreshes the sections
  whose content actually changes.

  Each section that can change renders a marker (snippets/sm_variant-content-
  marker.liquid): a JSON map of variant id -> content key, plus the key the
  section was rendered with. On a variant change:

  - Content sections: every section whose key differs is re-rendered for the
    new variant through the Section Rendering API (5 per request) and
    cross-faded in. Sections whose key is unchanged are left alone, so a
    product whose variants share content never makes a request.
  - The product section: product-info.js has already fetched it for the new
    variant, so the Overview blocks (bracketed with <!--sm-vc:id--> comments)
    are swapped straight from that HTML. The form, price and variant picker are
    never replaced.

  Swapped content is initialised the way the theme editor re-initialises a
  section: accordions and tabs are bound here, swipers by custom.js's own
  MutationObserver. Scroll-reveal animations are cancelled on new content -
  the cross-fade replaces them.
*/
(() => {
  if (window.smVariantContent) return;
  window.smVariantContent = true;

  const FADE_OUT = 180;
  const FADE_IN = 280;
  const SECTIONS_PER_REQUEST = 5;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  let requestToken = 0;
  let abortController = null;

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  function mainProductInfo() {
    return document.querySelector('product-info[id^="MainProduct-"]:not([data-original-section])');
  }

  function readMarker(marker) {
    try {
      return JSON.parse(marker.textContent).variants || {};
    } catch (error) {
      return {};
    }
  }

  // Marker scripts are inert JSON; everything else must be re-created to run.
  function runScripts(root) {
    root.querySelectorAll('script:not([type="application/json"])').forEach((oldScript) => {
      const script = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attr) => script.setAttribute(attr.name, attr.value));
      script.textContent = oldScript.textContent;
      oldScript.replaceWith(script);
    });
  }

  function initialise(root) {
    if (root.matches('.scroll-trigger')) root.classList.add('scroll-trigger--cancel');
    root.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.add('scroll-trigger--cancel'));
    if (root.matches('script:not([type="application/json"])')) {
      const holder = document.createElement('div');
      root.replaceWith(holder);
      holder.append(root);
      runScripts(holder);
      holder.replaceWith(...holder.childNodes);
      return;
    }
    runScripts(root);
    if (typeof window.initAccordion === 'function') window.initAccordion(root);
    if (typeof window.initTabs === 'function') window.initTabs(root);
  }

  /*
    Fades `targets` out, runs `swap`, then fades the new content in. With
    animateHeight the container's height also eases from the old size to the
    new one, so content below glides rather than jumps - including a section
    appearing or hiding. The product section skips that: clipping it would
    break its sticky info column mid-transition.

    Swaps on the same container are queued, so a quick second variant change
    never overlaps a transition that is still running.
  */
  function crossfade(container, getTargets, swap, { animateHeight = true } = {}) {
    const run = () => transition(container, getTargets, swap, animateHeight);
    container.smVariantQueue = (container.smVariantQueue || Promise.resolve()).then(run, run);
    return container.smVariantQueue;
  }

  async function transition(container, getTargets, swap, animateHeight) {
    if (reduceMotion.matches) {
      swap();
      return;
    }

    const startHeight = container.offsetHeight;
    if (animateHeight) {
      container.style.height = `${startHeight}px`;
      container.style.overflow = 'hidden';
    }

    const outgoing = getTargets();
    outgoing.forEach((el) => {
      el.style.transition = `opacity ${FADE_OUT}ms ease`;
      el.style.opacity = '0';
    });
    await wait(FADE_OUT);

    swap();

    const incoming = getTargets();
    incoming.forEach((el) => {
      el.style.transition = 'none';
      el.style.opacity = '0';
    });

    if (animateHeight) {
      container.style.height = 'auto';
      const endHeight = container.offsetHeight;
      container.style.height = `${startHeight}px`;
      container.offsetHeight; // commit the start height before transitioning

      container.style.transition = `height ${FADE_IN}ms ease`;
      container.style.height = `${endHeight}px`;
    }
    incoming.forEach((el) => {
      el.style.transition = `opacity ${FADE_IN}ms ease`;
      el.style.opacity = '1';
    });
    await wait(FADE_IN);

    if (animateHeight) {
      container.style.removeProperty('height');
      container.style.removeProperty('overflow');
      container.style.removeProperty('transition');
    }
    incoming.forEach((el) => {
      el.style.removeProperty('transition');
      el.style.removeProperty('opacity');
    });
    [container, ...incoming].forEach((el) => {
      if (el.getAttribute('style') === '') el.removeAttribute('style');
    });
  }

  /* ---------- Content sections ---------- */

  function swapSection(wrapper, html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const incoming = parsed.getElementById(wrapper.id);
    if (!incoming) return;

    return crossfade(
      wrapper,
      () => Array.from(wrapper.children),
      () => {
        wrapper.replaceChildren(...Array.from(incoming.childNodes));
        initialise(wrapper);
      }
    );
  }

  async function refreshSections(sectionIds, productUrl, variantId, token, signal) {
    for (let i = 0; i < sectionIds.length; i += SECTIONS_PER_REQUEST) {
      const batch = sectionIds.slice(i, i + SECTIONS_PER_REQUEST);
      const url = `${productUrl}${productUrl.includes('?') ? '&' : '?'}variant=${variantId}&sections=${batch.join(',')}`;

      let sections;
      try {
        sections = await fetch(url, { signal }).then((response) => response.json());
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
        return;
      }
      if (token !== requestToken) return;

      batch.forEach((sectionId) => {
        const wrapper = document.getElementById(`shopify-section-${sectionId}`);
        if (wrapper && sections[sectionId]) swapSection(wrapper, sections[sectionId]);
      });
    }
  }

  /* ---------- Product section Overview blocks ---------- */

  // [start, end] comment pairs for every <!--sm-vc:id--> region under root,
  // in document order.
  function overviewRegions(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    const open = new Map();
    const regions = [];
    while (walker.nextNode()) {
      const text = walker.currentNode.data.trim();
      if (text.startsWith('sm-vc:')) {
        open.set(text.slice(6), walker.currentNode);
      } else if (text.startsWith('/sm-vc:')) {
        const id = text.slice(7);
        if (open.has(id)) {
          regions.push({ id, start: open.get(id), end: walker.currentNode });
          open.delete(id);
        }
      }
    }
    return regions;
  }

  function regionNodes({ start, end }) {
    const nodes = [];
    for (let node = start.nextSibling; node && node !== end; node = node.nextSibling) nodes.push(node);
    return nodes;
  }

  function swapOverview(productInfo, html) {
    const currentRegions = overviewRegions(productInfo);
    const incomingRoot = html.querySelector('product-info') || html;
    const incomingRegions = overviewRegions(incomingRoot);
    if (!currentRegions.length || currentRegions.length !== incomingRegions.length) return;

    const container = productInfo.querySelector('.product') || productInfo;
    const elementsIn = (regions) =>
      regions.flatMap((region) => regionNodes(region)).filter((node) => node.nodeType === Node.ELEMENT_NODE);

    return crossfade(
      container,
      () => elementsIn(overviewRegions(productInfo)),
      () => {
        // Re-read the regions: an earlier queued swap may have replaced them.
        overviewRegions(productInfo).forEach((region, index) => {
          if (!incomingRegions[index]) return;
          regionNodes(region).forEach((node) => node.remove());
          const fresh = regionNodes(incomingRegions[index]).map((node) => document.importNode(node, true));
          region.end.before(...fresh);
          fresh.forEach((node) => node.nodeType === Node.ELEMENT_NODE && initialise(node));
        });
      },
      { animateHeight: false }
    );
  }

  /* ---------- Variant change ---------- */

  function onVariantChange({ data }) {
    const productInfo = mainProductInfo();
    const variantId = data?.variant?.id;
    if (!productInfo || !variantId || data.sectionId !== productInfo.dataset.section) return;

    // Any request still in flight is for a variant that is no longer
    // selected - drop it, even when this change needs no request of its own.
    abortController?.abort();
    const token = ++requestToken;

    const changed = [];
    document.querySelectorAll('script[data-variant-content]').forEach((marker) => {
      const nextKey = readMarker(marker)[variantId] || '';
      if (nextKey === (marker.dataset.currentKey || '')) return;
      changed.push({ marker, sectionId: marker.dataset.sectionId, nextKey });
    });
    if (!changed.length) return;

    const productChange = changed.find((entry) => entry.sectionId === productInfo.dataset.section);
    if (productChange && data.html) {
      productChange.marker.dataset.currentKey = productChange.nextKey;
      swapOverview(productInfo, data.html);
    }

    const sectionIds = changed
      .filter((entry) => entry !== productChange)
      .map((entry) => entry.sectionId);
    if (!sectionIds.length) return;

    abortController = new AbortController();
    refreshSections(sectionIds, productInfo.dataset.url, variantId, token, abortController.signal);
  }

  function start() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
